import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { PaymentMethodAddEditOperationType } from '@amfam/billing/payment-method/util/lib/models/payment-method-util.models';

import {
  GetFinancialInstitutionResponse,
  GetPCIAuthorizationTokenResponse,
  SavePaymentMethodResponse
} from '../../models/financial-account.model';
import { AddEditPaymentMethodPayload } from '../../models/payment-method.model';
import { FinancialAccountService } from '../../services/financial-account.service';
import {
  getFinancialInstitution as FinancialAccountActionsGetFinancialInstitution,
  getPCIAuthorizationToken as FinancialAccountActionsGetPCIAuthorizationToken,
  savePaymentMethod as FinancialAccountActionsSavePaymentMethod,
  getFinancialInstitutionFailure,
  getFinancialInstitutionSuccess,
  getPCIAuthorizationTokenFailure,
  getPCIAuthorizationTokenSuccess,
  savePaymentMethodFailure,
  savePaymentMethodSuccess
} from '../financial-account/financial-account.actions';
import { addPaymentMethod, editPaymentMethod } from '../payment-method/payment-method.actions';
import { selectPCIAuthorizationToken } from './financial-account.selectors';

export const getPCIAuthorizationToken = createEffect(
  (actions$ = inject(Actions), financialAccountService = inject(FinancialAccountService)) => {
    return actions$.pipe(
      ofType(FinancialAccountActionsGetPCIAuthorizationToken),
      switchMap(action =>
        financialAccountService.getPCIAuthorizationToken().pipe(
          map((response: GetPCIAuthorizationTokenResponse) =>
            getPCIAuthorizationTokenSuccess({
              response,
              correlationId: action.correlationId
            })
          ),
          catchError(error =>
            of(
              getPCIAuthorizationTokenFailure({
                error,
                correlationId: action.correlationId
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);

export const getFinancialInstitution = createEffect(
  (
    actions$ = inject(Actions),
    financialAccountService = inject(FinancialAccountService),
    store = inject(Store)
  ) => {
    return actions$.pipe(
      ofType(FinancialAccountActionsGetFinancialInstitution),
      concatLatestFrom(() => store.select(selectPCIAuthorizationToken)),
      switchMap(([action, pciToken]) =>
        financialAccountService
          .getFinancialInstitution(action.payload.routingNumber, pciToken)
          .pipe(
            map((response: GetFinancialInstitutionResponse) =>
              getFinancialInstitutionSuccess({
                response,
                correlationId: action.correlationId
              })
            ),
            catchError(error =>
              of(
                getFinancialInstitutionFailure({
                  error,
                  correlationId: action.correlationId
                })
              )
            )
          )
      )
    );
  },
  { functional: true }
);

export const savePaymentMethod = createEffect(
  (
    actions$ = inject(Actions),
    financialAccountService = inject(FinancialAccountService),
    store = inject(Store)
  ) => {
    return actions$.pipe(
      ofType(FinancialAccountActionsSavePaymentMethod),
      concatLatestFrom(() => store.select(selectPCIAuthorizationToken)),
      switchMap(([action, pciToken]) =>
        financialAccountService.savePaymentMethod(action.savePayload, pciToken).pipe(
          switchMap((response: SavePaymentMethodResponse) => {
            // append token in response to next call
            const addEditPayload: AddEditPaymentMethodPayload = {
              ...action.addEditPayload,
              paymentAccount: {
                ...action.addEditPayload.paymentAccount,
                token: response.finAcctServiceResponse.tokenId
              }
            };
            const addEditAction =
              action.operation === PaymentMethodAddEditOperationType.ADD
                ? addPaymentMethod({
                    correlationId: action.correlationId,
                    payload: addEditPayload
                  })
                : editPaymentMethod({
                    correlationId: action.correlationId,
                    payload: addEditPayload,
                    paymentAccountId: action.paymentAccountId ?? ''
                  });
            return [
              savePaymentMethodSuccess({
                response,
                correlationId: action.correlationId
              }),
              addEditAction
            ];
          }),
          catchError(error =>
            of(
              savePaymentMethodFailure({
                error,
                correlationId: action.correlationId
              })
            )
          )
        )
      )
    );
  },
  { functional: true }
);
